import type { FC } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";
import type { IHelpsiteArticle } from "@types-contentful";
import { colours, device } from "@helpers";
import { helpsiteTrackLink } from "@helpers/helpsite-mixpanel";
import Icon from "@components/Icon";

/* Match HelpTopicList: column, stacked, full width of page content */
const ArticleList = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 20px;
	width: 100%;
	box-sizing: border-box;

	@media (min-width: ${device.laptop}) {
		gap: 30px;
	}
`;

/* Same card style as TopicCard: bg, border, radius, padding. Title midnight blue (not red). */
const ArticleCard = styled(Link)`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 11px;
	padding: 15px 10px;
	background-color: rgba(255, 255, 255, 0.5);
	border: 1px solid rgba(0, 0, 0, 0.15);
	border-radius: 10px;
	text-decoration: none;
	color: inherit;
	width: 100%;
	box-sizing: border-box;
	transition: box-shadow 0.2s, border-color 0.2s;

	&:hover {
		box-shadow: 0 4px 12px rgba(18, 18, 78, 0.08);
		border-color: rgba(18, 18, 78, 0.25);
	}

	@media (min-width: ${device.laptop}) {
		padding: 20px;
		background-color: ${colours.WHITE};
		justify-content: flex-start;
	}
`;

/* Same as HelpTopicList IconContainer: 50x50, #f0f0f0, border-radius 6px */
const IconContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 50px;
	height: 50px;
	background-color: #f0f0f0;
	border-radius: 6px;
	flex-shrink: 0;
`;

const ArticleContent = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
	text-align: left;

	@media (min-width: ${device.laptop}) {
		text-align: left;
	}
`;

/* Midnight blue on both mobile and desktop (unlike topic cards which are red on mobile) */
const ArticleTitle = styled.h3`
	font-family: var(--euclid-medium);
	font-size: 18px;
	font-weight: 500;
	line-height: 1.2679999669392903em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
	text-align: left;

	@media (min-width: ${device.laptop}) {
		font-size: 20px;
		line-height: 1.268000030517578em;
	}
`;

const ArticlePreview = styled.p`
	font-family: var(--euclid-regular);
	font-size: 15px;
	font-weight: 400;
	line-height: 1.268000030517578em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;

	@media (min-width: ${device.laptop}) {
		font-size: 16px;
		line-height: 1.2680000066757202em;
	}
`;

interface HelpArticleListProps {
	articles: IHelpsiteArticle[];
	topicSlug?: string;
}

const HelpArticleList: FC<HelpArticleListProps> = ({ articles, topicSlug }) => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];
	const safeArticles = (articles || []).filter(
		(article) =>
			article?.fields?.articleRelativeUrl && article?.fields?.articleMainHeader,
	);

	return (
		<ArticleList>
			{safeArticles.map((article) => {
				const articleSlug = article.fields.articleRelativeUrl?.startsWith("/")
					? article.fields.articleRelativeUrl.slice(1)
					: article.fields.articleRelativeUrl || "";
				const articleUrl = topicSlug
					? `/help/${topicSlug}/${articleSlug}`
					: `/help/${articleSlug}`;

				return (
					<ArticleCard
						key={article.sys.id}
						href={articleUrl}
						onClick={() =>
							helpsiteTrackLink({
								helpsite_section: "article_list",
								page_path: pagePath,
								link_href: articleUrl,
								label: article.fields.articleMainHeader,
							})
						}
					>
						<IconContainer>
							<Icon
								iconName={article.fields.iconRef || "text"}
								iconSizes={{
									iconMobile: 40,
									iconTablet: 40,
									iconLaptop: 40,
									iconDesktop: 40,
									iconDesktopLarge: 40,
								}}
								alt={article.fields.articleMainHeader}
							/>
						</IconContainer>
						<ArticleContent>
							<ArticleTitle>{article.fields.articleMainHeader}</ArticleTitle>
							
								<ArticlePreview>{article.fields.subHeading}</ArticlePreview>
						
						</ArticleContent>
					</ArticleCard>
				);
			})}
		</ArticleList>
	);
};

export default HelpArticleList;
